/**
 * WHAT KIND, AND WHICH BRAND — ON THE SAME CARD.
 *
 * Chip rows that sit ABOVE the Qty row on the add card. No new screen, no
 * modal, nothing to dismiss. Everything is optional: a shopper who ignores all
 * of it taps Add once, exactly as before.
 *
 * What the household chose last time is already selected when the card opens,
 * and up to three previous combinations sit at the top as one-tap chips. That
 * is a pre-filled suggestion, never a question — the shopper still taps Add.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../ui/input';

/** The screen reads "Decaf"; the list keeps the grocer's own spelling. */
const show = (o, lang) => (lang === 'fr' ? (o.label_fr || o.label_en) : o.label_en) || o.value;

const EMPTY = { form: '', type: '', brand: '', note: '' };

export const useProductOptions = (api, session, lang, catalogId) => {
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);
    const [value, setValue] = useState(EMPTY);
    const load = useRef(null);

    useEffect(() => {
        let dead = false;
        if (!catalogId) { setData(null); setValue(EMPTY); return undefined; }
        load.current = async () => {
            try {
                const r = await api(`/options/${catalogId}`, { session, lang });
                if (dead) return;
                setData(r && r.group ? r : null);
                setFailed(false);
                setValue(r && r.preselect ? { ...EMPTY, ...r.preselect } : EMPTY);
            } catch (_e) {
                // "no choices" and "could not read the choices" are not the same.
                if (!dead) { setData(null); setFailed(true); }
            }
        };
        load.current();
        return () => { dead = true; };
    }, [api, session, lang, catalogId]);

    return { options: data, failed, value, setValue, reload: () => load.current?.() };
};

const Chip = ({ on, children, onClick, testId }) => (
    <button onClick={onClick} data-testid={testId} aria-pressed={on}
        className={`min-h-[44px] rounded-full border-2 px-4 text-sm font-semibold transition-colors ${on
            ? 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-100'
            : 'border-slate-300 text-foreground hover:border-emerald-400 dark:border-slate-600'}`}>
        {children}
    </button>
);

const Column = ({ col, value, onPick, onTyped, customMax, lang }) => {
    const { t } = useTranslation();
    const [more, setMore] = useState(false);
    const [q, setQ] = useState('');
    const [typed, setTyped] = useState('');
    const label = col.id === 'brand' ? t('grocery.optBrand')
        : col.id === 'form' ? (col.kind_label ? t('grocery.optKind') : t('grocery.optForm'))
            : t('grocery.optType');
    const list = more
        ? col.all.filter((o) => show(o, lang).toLowerCase().includes(q.trim().toLowerCase())
            || o.value.toLowerCase().includes(q.trim().toLowerCase()))
        : col.top;
    return (
        <div className="space-y-1.5" data-testid={`grocery-opt-col-${col.id}`}>
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                {label}
            </p>
            <div className="flex flex-wrap gap-1.5">
                {col.id === 'brand' ? (
                    <Chip on={!value} onClick={() => onPick('')}
                        testId="grocery-opt-brand-any">{t('grocery.optAnyBrand')}</Chip>
                ) : null}
                {list.map((o) => (
                    <Chip key={o.value} on={value === o.value}
                        testId={`grocery-opt-${col.id}-${o.value}`}
                        onClick={() => onPick(value === o.value ? '' : o.value)}>
                        {show(o, lang)}
                    </Chip>
                ))}
                {col.all.length > col.top.length ? (
                    <Chip on={more} testId={`grocery-opt-more-${col.id}`}
                        onClick={() => { setMore(!more); setQ(''); }}>
                        {more ? t('grocery.optLess') : t('grocery.optMore')}
                    </Chip>
                ) : null}
            </div>
            {more ? (
                <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)}
                        data-testid={`grocery-opt-search-${col.id}`}
                        placeholder={t('grocery.optSearch')}
                        className="h-11 pl-9 text-base" />
                </div>
            ) : null}
            <div className="flex items-center gap-1.5">
                <Input value={typed} maxLength={customMax}
                    onChange={(e) => setTyped(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        if (typed.trim()) onTyped(typed.trim(), setTyped);
                    }}
                    data-testid={`grocery-opt-type-${col.id}`}
                    placeholder={t('grocery.optTypeHint')}
                    className="h-11 flex-1 text-base" />
                <button onClick={() => typed.trim() && onTyped(typed.trim(), setTyped)}
                    data-testid={`grocery-opt-type-save-${col.id}`}
                    aria-label={t('grocery.optTypeSave')}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border-2 border-input text-foreground">
                    <Check className="h-5 w-5" />
                </button>
            </div>
        </div>
    );
};

export const ProductOptionsCard = ({ api, session, lang, catalogId, options, value, setValue,
    reload, testId = 'grocery-product-options' }) => {
    const { t } = useTranslation();
    if (!options || !(options.columns || []).length) return null;

    const pick = (col, v) => setValue({ ...value, [col]: v });

    const typeIn = async (col, text, clear) => {
        try {
            const r = await api(`/options/${catalogId}/custom`, {
                method: 'POST', session, lang, body: { column: col, value: text },
            });
            setValue({ ...value, [col]: r.value });
            clear('');
        } catch (e) {
            toast.error(e.message === 'too_long' ? t('grocery.optTooLong')
                : e.message === 'bad_characters' ? t('grocery.optBadText')
                    : t('grocery.optSaveFailed'));
        }
    };

    const forget = async (id) => {
        try {
            await api(`/options/history/${id}`, { method: 'DELETE', session, lang });
            reload();
        } catch (_e) { toast.error(t('grocery.optSaveFailed')); }
    };

    const labelOf = (colId, v) => {
        const col = (options.columns || []).find((c) => c.id === colId);
        const hit = col && (col.all || []).find((o) => o.value === v);
        return hit ? show(hit, lang) : v;
    };
    const chosen = [value.brand, labelOf('type', value.type), labelOf('form', value.form)]
        .filter(Boolean);

    return (
        <div className="space-y-3" data-testid={testId}>
            {(options.recent || []).length ? (
                <div className="space-y-1.5" data-testid="grocery-opt-recent">
                    <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                        {t('grocery.optAgain')}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {options.recent.map((r) => (
                            <span key={r.id} className="relative inline-flex">
                                <Chip testId={`grocery-opt-recent-${r.id}`}
                                    on={value.brand === r.brand && value.type === r.type
                                        && value.form === r.form}
                                    onClick={() => setValue({ form: r.form, type: r.type,
                                        brand: r.brand, note: r.note })}>
                                    <span className="pr-4">
                                        {(lang === 'fr' ? (r.label_fr || r.label_en) : r.label_en)
                                            || t('grocery.optPlain')}
                                    </span>
                                </Chip>
                                <button onClick={() => forget(r.id)}
                                    data-testid={`grocery-opt-forget-${r.id}`}
                                    aria-label={t('grocery.optForget')}
                                    className="absolute right-1 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:text-foreground">
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
            ) : null}

            {(options.columns || []).map((col) => (
                <Column key={col.id} col={col} value={value[col.id] || ''} lang={lang}
                    customMax={options.custom_max}
                    onPick={(v) => pick(col.id, v)}
                    onTyped={(text, clear) => typeIn(col.id, text, clear)} />
            ))}

            <div className="space-y-1.5">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    {t('grocery.optNote')}
                </p>
                <Input value={value.note || ''} maxLength={options.note_max || 60}
                    onChange={(e) => setValue({ ...value, note: e.target.value })}
                    data-testid="grocery-opt-note"
                    placeholder={t('grocery.optNoteHint')}
                    className="h-11 text-base" />
            </div>

            <div className="flex items-center justify-between gap-2">
                <p className="min-w-0 flex-1 break-words text-sm font-semibold text-foreground"
                    data-testid="grocery-opt-summary">
                    {chosen.length ? chosen.join(' · ') : t('grocery.optAnyIsFine')}
                </p>
                {chosen.length || value.note ? (
                    <button onClick={() => setValue(EMPTY)} data-testid="grocery-opt-clear"
                        className="min-h-[44px] shrink-0 px-2 text-sm font-semibold text-emerald-600">
                        {t('grocery.optClear')}
                    </button>
                ) : null}
            </div>
        </div>
    );
};

export default ProductOptionsCard;
